import React, { useEffect, useRef } from 'react';

const DISPLAY_MATH_RE = /(\\\[[\s\S]*?\\\]|\$\$[\s\S]*?\$\$)/g;
const LATEX_COMMAND_RE = /\\(?:frac|sqrt|partial|sigma|alpha|beta|gamma|delta|theta|pi|cdot|times|leq|geq|neq|infty|sum|int|left|right|text)\b/;
const INLINE_MATH_RE = /\\\([\s\S]*?\\\)|(?<!\$)\$(?!\$)[^$\n]+?\$(?!\$)/;

/**
 * Fix stray *, **, **** and unclosed markers from model output.
 * Skips lines that are part of LaTeX (display delimiter lines or heavy TeX commands).
 * partial=true trims incomplete markers at the end (for typewriter).
 */
export function normalizeChatMarkdown(text, { partial = false } = {}) {
  if (!text) return '';

  const lines = text.split('\n');
  return lines
    .map((line) => {
      const trimmed = line.trim();
      if (trimmed === '\\[' || trimmed === '\\]' || trimmed === '$$' || LATEX_COMMAND_RE.test(line)) {
        return line.replace(/\s+$/, '');
      }
      return normalizeLineMarkdown(line, partial);
    })
    .join('\n');
}

function normalizeLineMarkdown(line, partial) {
  let out = line.replace(/\s+$/, '');

  if (LATEX_COMMAND_RE.test(out) || /^\\[\[\(]/.test(out.trim()) || /\\[\]\)]$/.test(out.trim())) {
    return out;
  }

  out = out.replace(/^\*(\d+\.\s+)/, '$1');
  out = out.replace(/^\*\s*(\*\*)/, '$1');
  out = out.replace(/^\*([^*\n]+)\*\*(?!\*)/, '**$1**');
  // Repair the inverse mismatch inside a sentence or numbered item. A single
  // opener followed by a double-star closer otherwise leaves the final star
  // visible (for example: "1. *Exam Apps**:").
  out = out.replace(/(?<!\*)\*([^\s*\n][^*\n]*)\*\*(?!\*)/g, '*$1*');
  // Repair a double-star opener closed by a single star. Without this,
  // renderInline treats the second opening star as italics and displays the
  // first one literally (for example: "**What you will find:*").
  out = out.replace(/\*\*([^\s*\n][^*\n]*)\*(?!\*)/g, '*$1*');
  out = out.replace(/(\*\*[^*\n]+\*\*)\*+/g, '$1');
  out = out.replace(/(?<!\*)(\*[^*\n]+\*)\*+(?!\*)/g, '$1');
  out = out.replace(/\*{3,}/g, '**');

  const doubleCount = (out.match(/\*\*/g) || []).length;
  if (doubleCount % 2 !== 0) {
    out = out.replace(/\*\*([^*\n]*)$/, '$1');
    if (!partial) {
      out = out.replace(/\*\*$/, '');
    }
  }

  out = out.replace(/(?<!\*)\*(?!\*)$/, '');
  return out;
}

/** Strip \\[ \\] or $$ delimiters from a display-math segment. */
export function stripDisplayMathDelimiters(content) {
  const trimmed = (content || '').trim();
  if (trimmed.startsWith('\\[') && trimmed.endsWith('\\]')) {
    return trimmed.slice(2, -2).trim();
  }
  if (trimmed.startsWith('$$') && trimmed.endsWith('$$')) {
    return trimmed.slice(2, -2).trim();
  }
  return trimmed;
}

/** Normalize multiline display math so delimiters and body stay in one block. */
export function normalizeDisplayMathBlocks(text) {
  if (!text) return text;
  return text.replace(/\\\[\s*\n+([\s\S]*?)\n+\s*\\\]/g, (_match, inner) => `\\[\n${inner.trim()}\n\\]`);
}

/** Wrap bare TeX lines (e.g. \\frac{...}) in display delimiters so MathJax can render them. */
export function ensureChatLatexDelimiters(text) {
  if (!text || typeof text !== 'string') return text || '';

  const blocks = text.split(/\n\n+/);
  return blocks.map((block) => {
    const trimmed = block.trim();
    if (!trimmed) return block;
    if (/\\\[|\$\$|\\\(/.test(trimmed)) {
      return block;
    }
    if (LATEX_COMMAND_RE.test(trimmed) && !trimmed.includes('**') && trimmed.length < 800) {
      return `\\[\n${trimmed}\n\\]`;
    }
    return block;
  }).join('\n\n');
}

/** Full preprocessing pipeline before splitting/rendering math. */
export function prepareChatMessageForMath(text) {
  if (!text) return '';
  return normalizeDisplayMathBlocks(ensureChatLatexDelimiters(text));
}

/**
 * Split message into alternating plain-text and display-math segments.
 * Display math must stay in one DOM node so MathJax can match \\[ ... \\] / $$ ... $$.
 */
export function splitDisplayMathSegments(text, { partial = false } = {}) {
  if (!text) return [{ type: 'text', content: '' }];

  const parts = [];
  let lastIndex = 0;
  let match;
  const re = new RegExp(DISPLAY_MATH_RE.source, 'g');

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'display-math', content: match[1] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    const rest = text.slice(lastIndex);
    if (partial) {
      const openBracket = rest.indexOf('\\[');
      const openDollar = rest.indexOf('$$');
      const openAt = [openBracket, openDollar].filter((i) => i >= 0).sort((a, b) => a - b)[0];
      if (openAt !== undefined && openAt >= 0) {
        if (openAt > 0) {
          parts.push({ type: 'text', content: rest.slice(0, openAt) });
        }
        parts.push({ type: 'display-math-partial', content: rest.slice(openAt) });
      } else {
        parts.push({ type: 'text', content: rest });
      }
    } else {
      parts.push({ type: 'text', content: rest });
    }
  }

  return parts.length ? parts : [{ type: 'text', content: text }];
}

function lineHasInlineMath(line) {
  return INLINE_MATH_RE.test(line);
}

function waitForMathJax(timeoutMs = 8000) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }
    const done = () => resolve();
    if (window.MathJax?.startup?.promise) {
      window.MathJax.startup.promise.then(done).catch(done);
      return;
    }
    if (window.MathJax?.typesetPromise || window.MathJax?.tex2chtmlPromise) {
      done();
      return;
    }
    const started = Date.now();
    const tick = window.setInterval(() => {
      if (window.MathJax?.typesetPromise || window.MathJax?.tex2chtmlPromise) {
        window.clearInterval(tick);
        done();
      } else if (Date.now() - started > timeoutMs) {
        window.clearInterval(tick);
        done();
      }
    }, 100);
  });
}

async function typesetInlineMath(rootEl) {
  if (!rootEl || typeof window === 'undefined' || !window.MathJax?.typesetPromise) return;
  if (!rootEl.isConnected) return;
  await waitForMathJax();

  const nodes = rootEl.querySelectorAll('.chat-agent-math-inline, .chat-agent-msg-formatted');
  const targets = Array.from(nodes).filter((el) => {
    if (!el?.isConnected) return false;
    const raw = el.textContent || '';
    return (raw.includes('$') || raw.includes('\\')) && !el.querySelector('mjx-container');
  });

  if (!targets.length) return;

  try {
    await window.MathJax.typesetPromise(targets);
  } catch {
    // Incomplete LaTeX while streaming — final pass runs when partial=false.
  }
}

function scheduleTypeset(rootEl, partial) {
  const delays = partial ? [600] : [0, 80, 200, 450];
  const timers = delays.map((delay) => window.setTimeout(() => {
    void typesetInlineMath(rootEl);
  }, delay));
  return () => timers.forEach((id) => window.clearTimeout(id));
}

function MathDisplayBlock({ content, partial }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !content) return undefined;

    let cancelled = false;

    const render = async () => {
      await waitForMathJax();
      if (cancelled || !el.isConnected) return;

      const mj = window.MathJax;
      const latex = stripDisplayMathDelimiters(content);

      if (!latex || !mj?.tex2chtmlPromise) {
        el.textContent = content;
        return;
      }

      try {
        const node = await mj.tex2chtmlPromise(latex, { display: true });
        if (cancelled || !el.isConnected) return;
        el.replaceChildren(node);
      } catch {
        if (!cancelled && el.isConnected) {
          el.textContent = content;
        }
      }
    };

    const delay = partial ? 400 : 0;
    const timerId = window.setTimeout(() => {
      void render();
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [content, partial]);

  return (
    <div
      ref={ref}
      className={`chat-agent-math-display${partial ? ' partial' : ''}`}
      aria-label="Mathematical equation"
    />
  );
}

function renderInline(text, keyPrefix = 'i') {
  if (!text) return null;

  if (lineHasInlineMath(text)) {
    return <span className="chat-agent-math-inline tex2jax_process">{text}</span>;
  }

  const parts = [];
  let buffer = '';
  let i = 0;
  let idx = 0;

  const flush = () => {
    if (buffer) {
      parts.push(buffer);
      buffer = '';
    }
  };

  while (i < text.length) {
    if (text[i] === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end !== -1) {
        flush();
        parts.push(
          <strong key={`${keyPrefix}-b-${idx}`}>{text.slice(i + 2, end)}</strong>
        );
        idx += 1;
        i = end + 2;
        continue;
      }
    }

    if (text[i] === '*' && text[i + 1] !== '*') {
      const end = text.indexOf('*', i + 1);
      if (end !== -1 && text[end + 1] !== '*') {
        flush();
        parts.push(
          <em key={`${keyPrefix}-e-${idx}`}>{text.slice(i + 1, end)}</em>
        );
        idx += 1;
        i = end + 1;
        continue;
      }
    }

    buffer += text[i];
    i += 1;
  }

  flush();
  return parts.length ? parts : text;
}

function renderTextBlock(blockText, partial, keyPrefix) {
  const normalized = normalizeChatMarkdown(blockText, { partial });
  const paragraphs = normalized.split(/\n\n+/).filter((b) => b.trim());

  return paragraphs.map((para, pi) => (
    <div key={`${keyPrefix}-p-${pi}`} className="chat-agent-msg-paragraph tex2jax_process">
      {para.split('\n').map((rawLine, li) => {
        const line = rawLine.replace(/\s+$/, '');
        if (!line.trim()) return null;

        const numMatch = line.match(/^(\d+)\.\s+(.*)$/);
        const isSub = !numMatch && /^\s{2,}\S/.test(line);
        const rawText = numMatch ? numMatch[2] : line.trim();
        const lineText = normalizeLineMarkdown(rawText, partial);

        return (
          <div
            key={`${keyPrefix}-l-${pi}-${li}`}
            className={`chat-agent-msg-line${numMatch ? ' numbered' : ''}${isSub ? ' indent' : ''}`}
          >
            {numMatch && (
              <span className="chat-agent-msg-line-num">{numMatch[1]}.</span>
            )}
            <span className="chat-agent-msg-line-body">
              {renderInline(lineText, `${keyPrefix}p${pi}l${li}`)}
            </span>
          </div>
        );
      })}
    </div>
  ));
}

/**
 * Render chat text with bold (**), italics (*), paragraphs, numbered lists, indents, and LaTeX (MathJax).
 */
export function FormattedChatMessage({ text, partial = false }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !text) return undefined;
    return scheduleTypeset(el, partial);
  }, [text, partial]);

  if (!text) return null;

  const prepared = prepareChatMessageForMath(text);
  const segments = splitDisplayMathSegments(prepared, { partial });

  return (
    <div ref={containerRef} className="chat-agent-msg-formatted mathjax-process tex2jax_process">
      {segments.map((seg, si) => {
        if (seg.type === 'display-math' || seg.type === 'display-math-partial') {
          return (
            <MathDisplayBlock
              key={`math-${si}-${seg.content.length}`}
              content={seg.content}
              partial={seg.type === 'display-math-partial'}
            />
          );
        }
        return (
          <React.Fragment key={`text-${si}`}>
            {renderTextBlock(seg.content, partial, `s${si}`)}
          </React.Fragment>
        );
      })}
    </div>
  );
}
